import { makeAutoObservable, runInAction } from "mobx";
import {
	HiveApiError,
	type ApiCreditLedgerEntry,
	type ApiCreditsProduct,
	type ApiWebTopupProduct,
	type CursorMeta,
} from "@hive/sdk";
import { i18next } from "../i18n";
import { PAID_ECONOMY_ENABLED } from "../config";
import { hive } from "../lib/apiClient";
import { errorMessage } from "../lib/format";
import { analyticsErrorCode, track } from "../lib/analytics";

const TOPUP_ORDER_KEY = "muhaha:topup-order";
const TOPUP_POLL_INTERVAL_MS = 2000;
const TOPUP_POLL_MAX_ATTEMPTS = 30;

function emptyMeta(): CursorMeta {
	return { nextCursor: null, hasMore: false };
}

export interface MubuxActivityEntry {
	transactionId: string;
	at: string | null;
	delta: number;
	kind: string;
}

export class WalletStore {
	creditsBalance = 0;
	freeRemaining = 0;
	ledger: ApiCreditLedgerEntry[] = [];
	meta: CursorMeta = emptyMeta();
	loading = false;
	loadingMore = false;
	loaded = false;
	error = "";

	// Paid economy (PAID_ECONOMY_ENABLED). Left at their empty defaults — and never
	// fetched — when the flag is off, so the credits-only wallet issues exactly the
	// two Credits requests it did before.
	mubux = 0;
	mubuxActivity: MubuxActivityEntry[] = [];
	mubuxActivityError = "";
	products: ApiCreditsProduct[] = [];
	topupProducts: ApiWebTopupProduct[] = [];
	buyingPackId = "";
	/** The web-topup tier whose checkout is being created. */
	topupPackId = "";
	topupPolling = false;
	/** 402 INSUFFICIENT_FUNDS on a pack buy → point at the top-up tiers. */
	needsTopUp = false;
	notice = "";
	redeemSwept = false;

	constructor() {
		makeAutoObservable(this, {}, { autoBind: true });
	}

	reset() {
		this.creditsBalance = 0;
		this.freeRemaining = 0;
		this.ledger = [];
		this.meta = emptyMeta();
		this.loading = false;
		this.loadingMore = false;
		this.loaded = false;
		this.error = "";
		this.mubux = 0;
		this.mubuxActivity = [];
		this.mubuxActivityError = "";
		this.products = [];
		this.topupProducts = [];
		this.buyingPackId = "";
		this.topupPackId = "";
		this.topupPolling = false;
		this.needsTopUp = false;
		this.notice = "";
		this.redeemSwept = false;
	}

	async load() {
		if (this.loading) return;
		this.loading = true;
		this.error = "";
		try {
			const [credits, ledger] = await Promise.all([
				hive.credits.balance(),
				hive.credits.ledger({ limit: 20 }),
			]);
			runInAction(() => {
				this.creditsBalance = credits.balance;
				this.freeRemaining = credits.freeRemaining;
				this.ledger = ledger.items;
				this.meta = ledger.meta;
				this.loaded = true;
			});
			if (PAID_ECONOMY_ENABLED) await this.loadPaidEconomy();
		} catch (error) {
			runInAction(() => {
				this.error = errorMessage(error, i18next.t("wallet.loadFailed"));
			});
		} finally {
			runInAction(() => {
				this.loading = false;
			});
		}
	}

	/**
	 * Mubux balance, store tiers and history. Every call tolerates failure on its
	 * own: an environment with the flag on but no Stripe key still shows Credits
	 * (`webTopupProducts` 503s with WEB_TOPUP_NOT_CONFIGURED) instead of failing
	 * the whole wallet.
	 */
	private async loadPaidEconomy() {
		let mubuxActivityError = "";
		// Recovery path: sweep any credits-pack entitlement left unredeemed by an
		// interrupted purchase (`redeemed: 0` is the normal answer).
		if (!this.redeemSwept) {
			this.redeemSwept = true;
			await hive.economy.redeem().catch(() => null);
		}
		const [wallet, products, topupProducts, mubuxActivity] = await Promise.all([
			hive.economy.wallet().catch(() => ({ mubux: 0 })),
			hive.credits.products().catch(() => [] as ApiCreditsProduct[]),
			hive.economy.webTopupProducts().catch(() => [] as ApiWebTopupProduct[]),
			hive.economy.mubuxActivity({ limit: 100 }).catch((error) => {
				mubuxActivityError = errorMessage(error, i18next.t("wallet.mubuxActivityLoadFailed"));
				return this.mubuxActivity;
			}),
		]);
		runInAction(() => {
			this.mubux = wallet.mubux;
			this.products = products;
			this.topupProducts = topupProducts;
			this.mubuxActivity = mubuxActivity;
			this.mubuxActivityError = mubuxActivityError;
		});
	}

	/** Refresh Credits after generation or an internally earned reward. */
	async refreshBalances() {
		try {
			const [credits, wallet] = await Promise.all([
				hive.credits.balance(),
				PAID_ECONOMY_ENABLED ? hive.economy.wallet() : Promise.resolve(null),
			]);
			runInAction(() => {
				this.creditsBalance = credits.balance;
				this.freeRemaining = credits.freeRemaining;
				if (wallet) this.mubux = wallet.mubux;
			});
		} catch {
			// A later load will reconcile a failed background refresh.
		}
	}

	/** Sync the global Mubux display from a completed purchase's server balance. */
	setMubuxBalance(mubux: number) {
		this.mubux = mubux;
	}

	async loadMore() {
		if (this.loadingMore || !this.meta.hasMore) return;
		this.loadingMore = true;
		try {
			const page = await hive.credits.ledger({ limit: 20, cursor: this.meta.nextCursor ?? undefined });
			runInAction(() => {
				this.ledger = [...this.ledger, ...page.items];
				this.meta = page.meta;
			});
		} catch (error) {
			runInAction(() => {
				this.error = errorMessage(error, i18next.t("common.loadMoreFailed"));
			});
		} finally {
			runInAction(() => {
				this.loadingMore = false;
			});
		}
	}

	/** Buy a credits pack with Mubux (one atomic PlayFab purchase + server redeem). */
	async buyPack(product: ApiCreditsProduct): Promise<boolean> {
		if (this.buyingPackId) return false;
		this.buyingPackId = product.id;
		this.error = "";
		this.notice = "";
		this.needsTopUp = false;
		try {
			const result = await hive.economy.buyPack(product.id, { idempotencyKey: crypto.randomUUID() });
			track("credit_pack_purchase_result", {
				product_id: product.id,
				amount: product.priceMubux,
				currency: "MUBUX",
				granted_credits: result.grantedCredits,
				result: "success",
			});
			runInAction(() => {
				this.mubux = result.mubux;
				this.creditsBalance = result.creditsBalance;
				this.notice = result.redeemed
					? i18next.t("wallet.packBought", { amount: result.grantedCredits })
					: i18next.t("wallet.packPending");
			});
			void this.load();
			return true;
		} catch (error) {
			track("credit_pack_purchase_result", {
				product_id: product.id,
				amount: product.priceMubux,
				currency: "MUBUX",
				granted_credits: product.grantCredits,
				result: "failure",
				error_code: analyticsErrorCode(error),
			});
			runInAction(() => {
				if (error instanceof HiveApiError && error.code === "INSUFFICIENT_FUNDS") {
					this.needsTopUp = true;
				} else {
					this.error = errorMessage(error, i18next.t("wallet.buyFailed"));
				}
			});
			return false;
		} finally {
			runInAction(() => {
				this.buyingPackId = "";
			});
		}
	}

	/** Buy a Mubux tier through Stripe Checkout — a full-page redirect to Stripe. */
	async startTopup(product: ApiWebTopupProduct) {
		if (this.topupPackId) return;
		this.topupPackId = product.id;
		this.error = "";
		try {
			const { orderId, url } = await hive.economy.webTopupCheckout({
				idempotencyKey: crypto.randomUUID(),
				packId: product.id,
				successUrl: `${window.location.origin}/wallet?topup=success`,
				cancelUrl: `${window.location.origin}/wallet?topup=cancel`,
			});
			sessionStorage.setItem(TOPUP_ORDER_KEY, orderId);
			track("checkout_started", {
				product_id: product.id,
				amount: product.usdCents / 100,
				currency: "USD",
				result: "success",
			});
			window.location.assign(url);
			// The page is navigating away — leaving topupPackId set keeps the button
			// from flashing back to clickable before it does.
		} catch (error) {
			track("checkout_started", {
				product_id: product.id,
				amount: product.usdCents / 100,
				currency: "USD",
				result: "failure",
				error_code: analyticsErrorCode(error),
			});
			runInAction(() => {
				this.error = errorMessage(error, i18next.t("wallet.topupFailed"));
				this.topupPackId = "";
			});
		}
	}

	/** Handle the Stripe return (`?topup=success|cancel`): poll the order until it settles. */
	async resumeTopup(result: "success" | "cancel") {
		const orderId = sessionStorage.getItem(TOPUP_ORDER_KEY);
		if (!orderId) return;
		if (result === "cancel") {
			track("topup_result", {
				order_id: orderId,
				currency: "MUBUX",
				result: "cancel",
			});
			sessionStorage.removeItem(TOPUP_ORDER_KEY);
			return;
		}
		this.topupPolling = true;
		try {
			for (let attempt = 0; attempt < TOPUP_POLL_MAX_ATTEMPTS; attempt++) {
				if (attempt > 0) await new Promise((resolve) => setTimeout(resolve, TOPUP_POLL_INTERVAL_MS));
				try {
					const order = await hive.economy.webTopupOrder(orderId);
					if (order.status === "paid") {
						track("topup_result", {
							order_id: orderId,
							amount: order.mubux,
							currency: "MUBUX",
							result: "success",
						});
						sessionStorage.removeItem(TOPUP_ORDER_KEY);
						runInAction(() => {
							this.notice = i18next.t("wallet.topupSuccess", { amount: order.mubux });
						});
						void this.load();
						return;
					}
					if (order.status === "expired") {
						track("topup_result", {
							order_id: orderId,
							currency: "MUBUX",
							result: "expired",
						});
						sessionStorage.removeItem(TOPUP_ORDER_KEY);
						runInAction(() => {
							this.error = i18next.t("wallet.topupFailed");
						});
						return;
					}
				} catch {
					// Transient read failure — keep polling; the loop's budget bounds it.
				}
			}
			// Still pending after the poll budget: the reconcile cron settles it later.
			track("topup_result", {
				order_id: orderId,
				currency: "MUBUX",
				result: "pending",
			});
			runInAction(() => {
				this.notice = i18next.t("wallet.topupProcessing");
			});
		} finally {
			runInAction(() => {
				this.topupPolling = false;
			});
		}
	}
}

export const walletStore = new WalletStore();
